import { Archive, ArchiveRestore, CircleCheck, Mail, Pencil, Plus } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { errorMessage } from "@/api/errors";
import {
  useAdminPrototypeBatches,
  usePatchPrototypeShare,
  useRecordConfirmation,
  useSendPrototypeShare,
  type AdminPrototypeBatch,
  type AdminPrototypeShare,
} from "@/api/hooks/prototypes";
import type { AdminOrgUser, ConfirmationMethod } from "@/api/types";
import { ErrorNotice } from "@/components/ErrorNotice";
import { Field, FormError } from "@/components/Field";
import { PageLoading } from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  CONFIRMATION_METHODS,
  confirmingOwners,
  currentVersion,
  nameRepeatsNumber,
  recipientOptions,
  type RecipientOption,
} from "@/features/prototypes/shares";
import { choiceInput, choiceRow } from "@/features/questionnaire/questions/choiceStyles";
import { formatDate } from "@/lib/dates";

export function RecipientChecklist({
  options,
  selected,
  onChange,
}: {
  options: RecipientOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 font-medium">{t("admin.prototype.recipients")}</legend>
      <p className="text-sm text-fg-muted">{t("admin.prototype.recipientsHint")}</p>
      {options.length === 0 ? <p className="text-sm text-fg-muted">{t("admin.prototype.noRecipients")}</p> : null}
      {options.map((o) => (
        <label key={o.id} className={choiceRow}>
          <input
            type="checkbox"
            className={choiceInput}
            checked={selected.includes(o.id)}
            disabled={!o.eligible}
            onChange={() => toggle(o.id)}
          />
          <span className="flex min-w-0 flex-col">
            <span>
              {o.name} <span className="break-all text-sm text-fg-muted">({o.email})</span>
            </span>
            {o.eligible ? null : <span className="text-sm text-fg-muted">{t("admin.prototype.notJoined")}</span>}
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function ResendDialog({
  projectId,
  users,
  share,
  onClose,
}: {
  projectId: string;
  users: AdminOrgUser[];
  share: AdminPrototypeShare;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const send = useSendPrototypeShare(projectId);
  const options = recipientOptions(users);
  const [recipients, setRecipients] = useState<string[]>(options.filter((o) => o.eligible).map((o) => o.id));
  const title = t("admin.prototype.resendTitle", { name: t("prototype.label", { batch: share.batch_number, version: share.version }) });
  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>
        <RecipientChecklist options={options} selected={recipients} onChange={setRecipients} />
        <FormError message={send.isError ? errorMessage(send.error, t("errors.generic")) : null} />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={send.isPending || recipients.length === 0}
            onClick={() =>
              send.mutate(
                { id: share.id, recipientIds: recipients },
                {
                  onSuccess: () => {
                    toast.success(t("admin.prototype.resent", { count: recipients.length }));
                    onClose();
                  },
                },
              )
            }
          >
            {t("admin.prototype.resendSubmit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const NOTE_MAX = 1000;

function RecordConfirmationDialog({
  projectId,
  users,
  share,
  onClose,
}: {
  projectId: string;
  users: AdminOrgUser[];
  share: AdminPrototypeShare;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const record = useRecordConfirmation(projectId);
  const owners = confirmingOwners(users);
  const [ownerId, setOwnerId] = useState(owners[0]?.id ?? "");
  const [method, setMethod] = useState<ConfirmationMethod>("phone");
  const [note, setNote] = useState("");
  const title = t("admin.prototype.recordTitle", { label: t("prototype.label", { batch: share.batch_number, version: share.version }) });
  const submit = () =>
    record.mutate(
      { shareId: share.id, body: { confirmed_by_user_id: ownerId, method, note: note.trim() || null } },
      {
        onSuccess: () => {
          toast.success(t("admin.prototype.recorded"));
          onClose();
        },
      },
    );
  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t("admin.prototype.recordIntro")}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          {owners.length === 0 ? (
            <p className="text-sm text-fg-muted">{t("admin.prototype.recordNoOwner")}</p>
          ) : (
            <Field label={t("admin.prototype.recordOwner")}>
              {(p) => (
                <Select {...p} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                  {owners.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          <Field label={t("admin.prototype.recordMethod")}>
            {(p) => (
              <Select {...p} value={method} onChange={(e) => setMethod(e.target.value as ConfirmationMethod)}>
                {CONFIRMATION_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {t(`admin.prototype.method.${m}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t("admin.prototype.recordNote")}>
            {(p) => <Textarea {...p} rows={3} maxLength={NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
          <FormError message={record.isError ? errorMessage(record.error, t("errors.generic")) : null} />
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={record.isPending || !ownerId}>
              {t("admin.prototype.recordSubmit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function emailedTo(share: AdminPrototypeShare): string[] {
  return [...new Set(share.sends.map((s) => s.name || s.email))];
}

function VersionItem({
  projectId,
  share,
  isCurrent,
  busy,
  onArchive,
  onResend,
  onRecord,
}: {
  projectId: string;
  share: AdminPrototypeShare;
  isCurrent: boolean;
  busy: boolean;
  onArchive: (archived: boolean) => void;
  onResend: () => void;
  onRecord: () => void;
}) {
  const { t } = useTranslation();
  const names = emailedTo(share);
  return (
    <li className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="font-semibold">{t("prototype.label", { batch: share.batch_number, version: share.version })}</h4>
          {isCurrent ? <Badge variant="success">{t("admin.prototype.currentBadge")}</Badge> : null}
          {share.confirmed_at ? (
            <Badge variant="info">{t("admin.prototype.confirmedOn", { date: formatDate(share.confirmed_at) })}</Badge>
          ) : null}
          {share.archived_at ? <Badge variant="grey">{t("admin.prototype.archivedBadge")}</Badge> : null}
        </div>
        <p className="text-sm text-fg-muted">{t("admin.prototype.sharedOn", { date: formatDate(share.created_at) })}</p>
        <a href={share.url} target="_blank" rel="noopener noreferrer" className="break-all text-sm">
          {share.url}
        </a>
        <p className="text-sm text-fg-muted">
          {names.length > 0 ? t("admin.prototype.emailedTo", { names: names.join(", ") }) : t("admin.prototype.notEmailed")}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        {isCurrent && !share.confirmed_at ? (
          <Button size="sm" variant="secondary" onClick={onRecord}>
            <CircleCheck aria-hidden="true" className="h-4 w-4" />
            {t("admin.prototype.recordConfirmation")}
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" asChild>
          <Link to={`/admin/projects/${projectId}/prototype/${share.id}`}>
            <Pencil aria-hidden="true" className="h-4 w-4" />
            {t("admin.prototype.edit")}
          </Link>
        </Button>
        <Button size="sm" variant="ghost" onClick={onResend}>
          <Mail aria-hidden="true" className="h-4 w-4" />
          {t("admin.prototype.resend")}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => onArchive(!share.archived_at)}>
          {share.archived_at ? <ArchiveRestore aria-hidden="true" className="h-4 w-4" /> : <Archive aria-hidden="true" className="h-4 w-4" />}
          {share.archived_at ? t("admin.prototype.unarchive") : t("admin.prototype.archive")}
        </Button>
      </div>
    </li>
  );
}

function BatchSection({
  projectId,
  batch,
  busy,
  onArchive,
  onResend,
  onRecord,
}: {
  projectId: string;
  batch: AdminPrototypeBatch;
  busy: boolean;
  onArchive: (share: AdminPrototypeShare, archived: boolean) => void;
  onResend: (share: AdminPrototypeShare) => void;
  onRecord: (share: AdminPrototypeShare) => void;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const current = currentVersion(batch.versions);
  const heading = nameRepeatsNumber(batch.name, batch.number)
    ? batch.name
    : t("admin.prototype.batchHeading", { number: batch.number, name: batch.name });
  const versions = [...batch.versions].sort((a, b) => b.version - a.version);
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4 py-5 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 id={headingId} className="text-lg">
            {heading}
          </h3>
          {current?.confirmed_at ? <Badge variant="success">{t("admin.prototype.confirmedBadge")}</Badge> : null}
        </div>
        <Button size="sm" variant="secondary" asChild>
          <Link to={`/admin/projects/${projectId}/prototype/new?batch=${batch.number}`}>
            <Plus aria-hidden="true" className="h-4 w-4" />
            {t("admin.prototype.shareNewVersion")}
          </Link>
        </Button>
      </div>
      <ul className="flex flex-col divide-y divide-border">
        {versions.map((s) => (
          <VersionItem
            key={s.id}
            projectId={projectId}
            share={s}
            isCurrent={s.id === current?.id}
            busy={busy}
            onArchive={(archived) => onArchive(s, archived)}
            onResend={() => onResend(s)}
            onRecord={() => onRecord(s)}
          />
        ))}
      </ul>
    </section>
  );
}

/** Admin › Project settings: prototype batches and their versions, newest first; share, re-send, confirm. */
export function PrototypeSharesCard({ projectId, users }: { projectId: string; users: AdminOrgUser[] }) {
  const { t } = useTranslation();
  const batches = useAdminPrototypeBatches(projectId);
  const patch = usePatchPrototypeShare(projectId);
  const [resending, setResending] = useState<AdminPrototypeShare | null>(null);
  const [recording, setRecording] = useState<AdminPrototypeShare | null>(null);
  const sorted = batches.data ? [...batches.data].sort((a, b) => b.number - a.number) : undefined;

  const setArchived = (share: AdminPrototypeShare, archived: boolean) =>
    patch.mutate(
      { id: share.id, body: { archived } },
      {
        onSuccess: () => toast.success(archived ? t("admin.prototype.archived") : t("admin.prototype.unarchived")),
        onError: (e) => toast.error(errorMessage(e, t("errors.generic"))),
      },
    );

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl">{t("admin.prototype.title")}</h2>
          <p className="text-sm text-fg-muted">{t("admin.prototype.intro")}</p>
        </div>
        <Button size="sm" asChild>
          <Link to={`/admin/projects/${projectId}/prototype/new`}>
            <Plus aria-hidden="true" className="h-4 w-4" />
            {t("admin.prototype.shareNew")}
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {batches.isPending ? <PageLoading /> : null}
        {batches.isError ? <ErrorNotice error={batches.error} /> : null}
        {sorted && sorted.length === 0 ? <p className="text-fg-muted">{t("admin.prototype.empty")}</p> : null}
        {sorted && sorted.length > 0 ? (
          <div className="flex flex-col divide-y divide-border">
            {sorted.map((b) => (
              <BatchSection
                key={b.id}
                projectId={projectId}
                batch={b}
                busy={patch.isPending}
                onArchive={setArchived}
                onResend={setResending}
                onRecord={setRecording}
              />
            ))}
          </div>
        ) : null}
      </CardContent>
      {resending ? <ResendDialog projectId={projectId} users={users} share={resending} onClose={() => setResending(null)} /> : null}
      {recording ? (
        <RecordConfirmationDialog projectId={projectId} users={users} share={recording} onClose={() => setRecording(null)} />
      ) : null}
    </Card>
  );
}
